import React, { useState } from 'react';
import Table from './Table';
import { reduceResolution, wbp } from 'Theme';
import { Box, TableCell, TableRow, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useCommonStyles as useTableStyles } from './Table/CommonStyles';
import { Enrollment, Membership } from '@spike/membership-model';
import { ToolbarButton } from 'components/UI/Toolbar/ToolbarButton';
import { ToolbarItem } from 'components/UI';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import moment from 'moment';
import EnrollmentStatusChip from 'components/Memberships/Enrollment/EnrollmentStatusChip';
import { getEnrollmentActions } from 'utils/EnrollmentUtils';
import CancelEnrollmentDialog from '../Enrollment/CancelEnrollmentDialog';
import { cancelEnrollmentThunk } from '@spike/memberships-action';
import { useDispatch } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { Pagination } from 'components/UI';
import useEnrollmentCheckout from 'hooks/useEnrollmentCheckout';
import EnrollmentCheckoutWrapper from '../Enrollment/EnrollmentCheckoutWrapper';
import ToolbarButtonWithPopper from 'components/UI/Toolbar/ToolbarButtonWithPopper';

export type TableColumn =
    | 'client'
    | 'membership'
    | 'pet'
    | 'type'
    | 'startDate'
    | 'endDate'
    | 'nextBilling'
    | 'status'
    | 'total'
    | 'actions';

interface MembershipDetailsTableProps {
    loading: boolean;
    membershipDetail?: Array<Membership> | null;
    enrollments: Array<Enrollment> | null;
    onEnrollmentClick: (enrollment: Enrollment) => void;
    pagination?: {
        currentPage: number;
        totalPages: number;
        totalEnrollments: number;
        size: number;
    } | null;
    onPageChange?: (page: number) => void;
    pageSize?: number;
    onPageSizeChange?: (size: number) => void;
    filterSelectedOption?: string;
    onUpdateEnrollment?: (enrollment: Enrollment) => void;
    onRetryPayment?: (enrollment: Enrollment) => void;
    onPaid: () => void;
    columns?: TableColumn[];
    emptyDescription?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        'root': {
            paddingTop: 8,

            [theme.breakpoints.down('sm')]: {
                backgroundColor: '#FAFAFA'
            },
            [theme.breakpoints.up('md')]: {
                paddingTop: 16,
                margin: '0px -24px'
            }
        },
        'header': {
            [theme.breakpoints.down(wbp)]: {
                paddingBottom: `${reduceResolution(63)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingBottom: '63px'
            }
        },
        'cell': {
            fontWeight: 400,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '15px'
            }
        },
        'cellHeader': {
            padding: '20px 0px'
        },
        'toolbarButton': {
            fontSize: 18,
            marginRight: 14,
            display: 'inline-block'
        },
        'toolbarCell': {
            width: '130px',
            maxWidth: '130px',
            paddingRight: '30px',
            textAlign: 'right'
        },
        'cellName': {
            width: '22%',
            maxWidth: '22%',
            [theme.breakpoints.down('sm')]: {
                width: '40%',
                maxWidth: '40%'
            }
        },
        'clientMembershipCell': {
            width: '14%',
            maxWidth: '14%'
        },
        'cellPet': {
            width: '12%',
            maxWidth: '12%'
        },
        'cellTotal': {
            width: '150px',
            maxWidth: '150px',
            textAlign: 'right'
        },
        'customBadge': {
            'display': 'inline-flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'borderRadius': 50,
            'color': '#fff',
            'fontSize': 15,
            'transform': 'none',
            'position': 'relative',
            'padding': '0px',
            'height': '27px',
            'fontWeight': 500,
            '& .MuiChip-label': {
                overflow: 'visible'
            }
        },
        'badgeActive': {
            backgroundColor: '#5E8677'
        },
        'badgePending': {
            backgroundColor: '#EAB464'
        },
        'badgeCanceled': {
            backgroundColor: '#EF4F57'
        },
        'badgePaused': {
            backgroundColor: '#F4F3F0',
            color: '#000'
        },
        'badgePastDue': {
            backgroundColor: '#FFE5E7',
            color: '#CC444B'
        },
        'enrollmentOptionsButton': {
            'all': 'unset',
            'fontSize': 21,
            '& svg': {
                display: 'block'
            }
        },
        'wrapperClassName': {
            overflow: 'visible',
            overflowX: 'auto',
            [theme.breakpoints.up('lg')]: {
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent'
            },
            [theme.breakpoints.down('md')]: {
                scrollbarWidth: 'none'
            }
        },
        'containerEmpty': {
            padding: '40px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
        },
        'imageEmpty': {
            width: '100%',
            maxWidth: '375px',
            display: 'inline-block'
        },
        'titleEmpty': {
            marginTop: '20px',
            fontSize: '22px',
            fontWeight: 600,
            color: '#000'
        },
        'descriptionEmpty': {
            marginTop: '10px',
            fontSize: '16px',
            color: '#000',
            maxWidth: '300px',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        'confirmQuestion': {
            fontSize: '16px',
            color: '#000',
            maxWidth: '445px',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 10
        },
        'select': {
            textAlign: 'left',
            paddingLeft: '20px',
            paddingRight: '20px',
            maxWidth: '95%',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 5,
            [theme.breakpoints.down('md')]: {
                maxWidth: '100%'
            }
        },
        '@global': {
            '[class*="makeStyles-select"] [class*="makeStyles-labelContainer"]': {
                'paddingLeft': 0,
                'paddingRight': 0,
                'marginLeft': 0,
                'marginRight': 0,
                'marginBottom': 5,
                '& .MuiTypography-body1': {
                    fontWeight: 600,
                    fontSize: '16px'
                }
            }
        },
        'row': {
            cursor: 'pointer'
        },
        'paginationContainer': {
            paddingRight: '35px',
            paddingLeft: '35px',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '15px',
            paddingBottom: '10px'
        }
    })
);

export const MembershipDetailsTable: React.FunctionComponent<MembershipDetailsTableProps> = props => {
    const classes = useStyles();
    const tableStyles = useTableStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [showMobileEnrollmentsDropdown, setShowMobileEnrollmentsDropdown] = useState(false);
    const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
    const [actionType, setActionType] = useState<string | null>(null);
    const [showCancelConfirmDialog, setShowCancelConfirmDialog] = useState(false);
    const { checkoutInfo, openCheckout, closeCheckout, handlePaymentSuccess } = useEnrollmentCheckout();
    const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
    const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    useHideHubSpotChat((showMobileEnrollmentsDropdown && !isLg) || (showCancelConfirmDialog && !isLg));

    const handleEnrollmentAction = (action: string, enrollment: Enrollment) => {
        setSelectedEnrollment(enrollment);
        setActionType(action);

        switch (action) {
            case 'update_card':
                props.onUpdateEnrollment?.(enrollment);
                break;
            case 'try_again':
                props.onRetryPayment?.(enrollment);
                break;
            case 'pause':
                // open drawer
                break;
            case 'cancel':
                setShowCancelConfirmDialog(true);
                break;
            case 'resume':
                // open drawer
                break;
            case 'collect_payment':
                openCheckout(enrollment);
                break;
            default:
                break;
        }
    };

    const handleConfirmCancel = (reason: string) => {
        if (!selectedEnrollment) return;
        const canceledAt = new Date().toISOString();
        dispatch(cancelEnrollmentThunk(apiClientWrapper, selectedEnrollment.id, canceledAt, reason));
        setShowCancelConfirmDialog(false);
    };

    const headersEnrollments = (
        <>
            {props.columns?.includes('membership') && (
                <TableCell className={clsx(tableStyles.headerCell, classes.cellName)}>Membership name</TableCell>
            )}

            {props.columns?.includes('client') && (
                <TableCell className={clsx(tableStyles.headerCell, classes.clientMembershipCell)}>Client</TableCell>
            )}

            {props.columns?.includes('pet') && (
                <TableCell className={clsx(tableStyles.headerCell, classes.cellPet)}>Pet</TableCell>
            )}
            {props.columns?.includes('type') && (
                <TableCell className={clsx(tableStyles.headerCell, tableStyles.headerCell)}>Type</TableCell>
            )}
            {props.columns?.includes('startDate') && (
                <TableCell className={tableStyles.headerCell}>Start Date</TableCell>
            )}
            {props.columns?.includes('endDate') && <TableCell className={tableStyles.headerCell}>End Date</TableCell>}
            {props.columns?.includes('nextBilling') && (
                <TableCell className={tableStyles.headerCell}>Next Billing</TableCell>
            )}
            {props.columns?.includes('status') && <TableCell className={tableStyles.headerCell}>Status</TableCell>}
            {props.columns?.includes('total') && (
                <TableCell className={clsx(tableStyles.headerCell, tableStyles.rightAlign)}>Total</TableCell>
            )}
            {props.columns?.includes('actions') && (
                <TableCell className={clsx(tableStyles.headerCell, classes.toolbarCell)}></TableCell>
            )}
        </>
    );

    const getNameCellClassName = () => {
        if (props.columns?.includes('membership') && props.columns?.includes('client')) {
            return classes.clientMembershipCell;
        }
        return classes.cellName;
    };

    const rowsEnrollments = (
        <>
            {props.enrollments?.map(enrollment => {
                return (
                    <TableRow
                        key={enrollment.id}
                        onClick={() => props.onEnrollmentClick(enrollment)}
                        className={classes.row}
                    >
                        {props.columns?.includes('membership') && (
                            <TableCell className={clsx(tableStyles.cell, classes.cellName)}>
                                {enrollment.membership.name}
                            </TableCell>
                        )}
                        {props.columns?.includes('client') && (
                            <TableCell className={clsx(tableStyles.cell, getNameCellClassName())}>
                                {enrollment.clientName}
                            </TableCell>
                        )}
                        {props.columns?.includes('pet') && (
                            <TableCell className={clsx(tableStyles.cell, classes.cellPet)}>
                                {' '}
                                {enrollment.pets.length > 1 ? `${enrollment.pets.length}` : enrollment.pets[0].name}
                            </TableCell>
                        )}
                        {props.columns?.includes('type') && (
                            <TableCell className={tableStyles.cell}>
                                {enrollment.membership.enrollmentType === 'package' ? 'Package' : 'Subscription'}
                            </TableCell>
                        )}
                        {props.columns?.includes('startDate') && (
                            <TableCell className={tableStyles.cell}>
                                {moment(enrollment.startDate).format('MMM DD, YYYY')}
                            </TableCell>
                        )}
                        {props.columns?.includes('endDate') && (
                            <TableCell className={tableStyles.cell}>
                                {moment(enrollment.endDate).isValid()
                                    ? moment(enrollment.endDate).format('MMM DD, YYYY')
                                    : String(enrollment.endDate)}
                            </TableCell>
                        )}
                        {props.columns?.includes('nextBilling') && (
                            <TableCell className={tableStyles.cell}>
                                {enrollment.nextBillingDate && enrollment.membership.enrollmentType != 'package'
                                    ? moment(enrollment.nextBillingDate).format('MMM DD, YYYY')
                                    : '-'}
                            </TableCell>
                        )}
                        {props.columns?.includes('status') && (
                            <TableCell className={tableStyles.cell}>
                                <EnrollmentStatusChip status={enrollment.status} />
                            </TableCell>
                        )}
                        {props.columns?.includes('total') && (
                            <TableCell className={clsx(tableStyles.cell, tableStyles.rightAlign)}>
                                ${Number(enrollment.totalAmount).toFixed(2)}
                            </TableCell>
                        )}
                        {props.columns?.includes('actions') && (
                            <TableCell className={clsx(tableStyles.cell, classes.toolbarCell)}>
                                {isLg && enrollment.status !== 'canceled' && (
                                    <ToolbarButtonWithPopper
                                        id={`enrollment_${enrollment.id}`}
                                        isOpen={openPopoverId === `enrollment_${enrollment.id}`}
                                        onToggle={() => {
                                            setOpenPopoverId(prev =>
                                                prev === `enrollment_${enrollment.id}`
                                                    ? null
                                                    : `enrollment_${enrollment.id}`
                                            );
                                        }}
                                        onClose={() => setOpenPopoverId(null)}
                                    >
                                        {getEnrollmentActions(
                                            enrollment.status,
                                            enrollment.membership.enrollmentType
                                        ).map((action, index) => (
                                            <ToolbarItem
                                                key={index}
                                                text={action.label}
                                                icon={action.icon}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleEnrollmentAction(action.action, enrollment);
                                                    setOpenPopoverId(null);
                                                }}
                                            />
                                        ))}
                                    </ToolbarButtonWithPopper>
                                )}

                                {!isLg && enrollment.status !== 'canceled' && (
                                    <button
                                        type="button"
                                        aria-label="Enrollment actions"
                                        className={classes.enrollmentOptionsButton}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setSelectedEnrollment(enrollment);
                                            setShowMobileEnrollmentsDropdown(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faEllipsis} />
                                    </button>
                                )}
                            </TableCell>
                        )}
                    </TableRow>
                );
            })}
        </>
    );

    const emptyEnrollments = () => {
        return (
            <Box className={classes.containerEmpty}>
                <img
                    className={classes.imageEmpty}
                    src="/images/memberships/clients-notfound.svg"
                />
                <Typography className={classes.titleEmpty}>No clients yet</Typography>
                {(props.filterSelectedOption === 'active' || props.filterSelectedOption === 'all') && (
                    <Typography className={classes.descriptionEmpty}>
                        {props.emptyDescription || "Once someone buys this membership, they'll appear here."}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box className={classes.root}>
            <Table
                headers={headersEnrollments}
                rows={props.enrollments && props.enrollments.length > 0 ? rowsEnrollments : <></>}
                loading={props.loading}
                withoutColor={true}
                wrapperClassName={classes.wrapperClassName}
            />

            <CancelEnrollmentDialog
                open={showCancelConfirmDialog}
                enrollment={selectedEnrollment}
                onCancel={() => setShowCancelConfirmDialog(false)}
                onConfirm={handleConfirmCancel}
                questionClassName={classes.confirmQuestion}
                subquestionClassName={classes.select}
            />

            {showMobileEnrollmentsDropdown && selectedEnrollment && !isLg && (
                <ActionsPopup
                    actions={getEnrollmentActions(
                        selectedEnrollment.status,
                        selectedEnrollment.membership.enrollmentType
                    ).map(action => ({
                        ...action,
                        onClick: () => {
                            setShowMobileEnrollmentsDropdown(false);
                            handleEnrollmentAction(action.action, selectedEnrollment);
                        }
                    }))}
                    onClose={() => setShowMobileEnrollmentsDropdown(false)}
                />
            )}

            {props.enrollments && !props.loading && props.enrollments.length === 0 && emptyEnrollments()}

            {props.pagination &&
                props.onPageChange &&
                !props.loading &&
                props.pagination.totalEnrollments > (props.pageSize || props.pagination.size || 10) && (
                    <Box className={classes.paginationContainer}>
                        <Pagination
                            count={props.pagination.totalEnrollments}
                            page={props.pagination.currentPage}
                            pageSize={props.pageSize || props.pagination.size || 10}
                            onPageChange={props.onPageChange}
                            onPageSizeChange={props.onPageSizeChange}
                        />
                    </Box>
                )}

            <EnrollmentCheckoutWrapper
                checkoutInfo={checkoutInfo}
                onClose={closeCheckout}
                onPaid={props.onPaid}
            />
        </Box>
    );
};

export default MembershipDetailsTable;
